import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import "./Campaign.css";
import "./CampDailyReport.css";
import { getCampReport, getMyCampReports, submitCampReport } from "../../api/admin/dashboard";
import CustomDatePicker from "../resuableComponents/CustomDatePicker";

const FIELDS = [
  {
    key: "actualCampsDone",
    title: "Camps completed",
    hint: "How many camps were actually done?",
  },
  {
    key: "campPatientCount",
    title: "Camp patients",
    hint: "How many patients were seen at the camps?",
  },
  {
    key: "prakritiReportsFiled",
    title: "Prakriti reports filed",
    hint: "How many prakriti parikshan reports were filed?",
  },
  {
    key: "prakritiReportsGenerated",
    title: "Prakriti reports generated",
    hint: "How many prakriti parikshan reports were generated?",
  },
  {
    key: "actualPatientsVisited",
    title: "Hospital visits from camp",
    hint: "How many camp patients actually visited the hospital?",
  },
  {
    key: "demoVisitsDone",
    title: "Demo visits",
    hint: "How many demo visits were done from the camps?",
  },
];

const emptyForm = () =>
  Object.fromEntries(FIELDS.map((field) => [field.key, ""]));

const today = () => dayjs();

const SalesDailyReport = () => {
  const [selectedDate, setSelectedDate] = useState(today);
  const [form, setForm] = useState(emptyForm);
  const [alreadySaved, setAlreadySaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  const dateValue = selectedDate?.isValid?.()
    ? selectedDate.format("YYYY-MM-DD")
    : today().format("YYYY-MM-DD");

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const response = await getMyCampReports();
      setHistory(response.data?.data?.reports || []);
    } catch (error) {
      console.error("Failed to load camp report history", error);
      setHistory([]);
      if (error.response?.status !== 404) {
        toast.error(error.response?.data?.message || "Could not load your earlier reports.");
      }
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    loadHistory();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadReport = async () => {
      setLoading(true);
      try {
        const response = await getCampReport(dateValue);
        if (cancelled) return;
        const data = response.data?.data;
        if (data?.report) {
          const next = emptyForm();
          FIELDS.forEach((field) => {
            next[field.key] = String(data.report[field.key] ?? "");
          });
          setForm(next);
          setAlreadySaved(true);
        } else {
          setForm(emptyForm());
          setAlreadySaved(false);
        }
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to load camp report", error);
        setForm(emptyForm());
        setAlreadySaved(false);
        if (error.response?.status !== 404) {
          toast.error(error.response?.data?.message || "Could not load this report.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadReport();
    return () => {
      cancelled = true;
    };
  }, [dateValue]);

  const handleCountChange = (key, raw) => {
    if (raw !== "" && !/^\d+$/.test(raw)) return;
    setForm((prev) => ({ ...prev, [key]: raw }));
  };

  const handleDateChange = (next) => {
    if (!next || !next.isValid()) {
      setSelectedDate(today());
      return;
    }
    if (next.isAfter(today(), "day")) return;
    setSelectedDate(next);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const missing = FIELDS.some((field) => form[field.key] === "");
    if (missing) {
      toast.error("Enter a whole number for every question.");
      return;
    }

    setSaving(true);
    try {
      const payload = Object.fromEntries(
        FIELDS.map((field) => [field.key, Number(form[field.key])])
      );
      await submitCampReport({ ...payload, reportDate: dateValue });
      setAlreadySaved(true);
      toast.success(
        alreadySaved
          ? `Report for ${dayjs(dateValue).format("DD MMM YYYY")} updated.`
          : `Report for ${dayjs(dateValue).format("DD MMM YYYY")} saved.`
      );
      loadHistory();
    } catch (error) {
      console.error("Failed to save camp report", error);
      toast.error(error.response?.data?.message || "Could not save this report.");
    } finally {
      setSaving(false);
    }
  };

  const formattedDate = dayjs(dateValue).format("DD MMM YYYY");
  const filledCount = FIELDS.filter((field) => form[field.key] !== "").length;
  const isToday = dateValue === today().format("YYYY-MM-DD");

  return (
    <div className="camp-surface">
      <form className="camp-shell sales-report-shell" onSubmit={handleSubmit}>
        <header className="sales-report-header">
          <div className="camp-hero-copy">
            <p className="camp-chip">Daily camp report</p>
            <h1>
              {isToday ? "Today's numbers" : "Report numbers"}
              <span> · {formattedDate}</span>
            </h1>
            <p className="daily-report-note">
              Pick the date, then enter whole numbers. You can update a saved day later.
            </p>
          </div>
          <div className="sales-report-side">
            <div className="daily-date-field">
              <span>Report date</span>
              <CustomDatePicker
                value={selectedDate}
                onChange={handleDateChange}
                maxDate={today()}
                placeholder="Select date"
                className="daily-date-input"
              />
            </div>
            <div className={`sales-status ${alreadySaved ? "saved" : "pending"}`}>
              <span className="sales-status-dot" />
              {alreadySaved ? "Saved for this date" : "Not submitted for this date"}
            </div>
          </div>
        </header>

        {loading ? (
          <div className="sales-report-list" aria-hidden="true">
            {FIELDS.map((field) => (
              <div className="sales-report-row skeleton" key={field.key}>
                <div className="sales-skeleton-line title" />
                <div className="sales-skeleton-line hint" />
              </div>
            ))}
          </div>
        ) : (
          <>
            <div className="sales-report-list">
              {FIELDS.map((field, index) => (
                <label className="sales-report-row" key={field.key}>
                  <span className="sales-report-index">{String(index + 1).padStart(2, "0")}</span>
                  <span className="sales-report-copy">
                    <span className="sales-report-title">{field.title}</span>
                    <span className="sales-report-hint">{field.hint}</span>
                  </span>
                  <input
                    className="sales-report-input"
                    type="text"
                    inputMode="numeric"
                    value={form[field.key]}
                    onChange={(event) => handleCountChange(field.key, event.target.value)}
                    placeholder="0"
                    aria-label={field.title}
                  />
                </label>
              ))}
            </div>
            <div className="sales-report-footer">
              <p className="daily-report-note">
                {filledCount} of {FIELDS.length} filled
              </p>
              <button className="daily-report-submit" type="submit" disabled={saving}>
                {saving ? "Saving…" : alreadySaved ? "Update report" : "Submit report"}
              </button>
            </div>
          </>
        )}

        <section className="sales-history">
          <div className="sales-history-head">
            <h2>Your submissions</h2>
            <p>Current and earlier reports. Select a row to open that date.</p>
          </div>
          <div className="camp-table-wrapper">
            <table className="camp-table sales-history-table">
              <thead>
                <tr>
                  <th>Report date</th>
                  {FIELDS.map((field) => (
                    <th key={field.key} className="daily-num-head">
                      {field.title}
                    </th>
                  ))}
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {historyLoading ? (
                  Array.from({ length: 3 }).map((_, index) => (
                    <tr key={`history-skeleton-${index}`} className="camp-skeleton-row">
                      {Array.from({ length: FIELDS.length + 2 }).map((__, cell) => (
                        <td key={cell}>
                          <div className="camp-skeleton-cell" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : history.length > 0 ? (
                  history.map((report) => {
                    const selected = report.reportDate === dateValue;
                    return (
                      <tr
                        key={report._id}
                        className={`sales-history-row${selected ? " selected" : ""}`}
                        onClick={() => handleDateChange(dayjs(report.reportDate))}
                      >
                        <td className="camp-nowrap">{dayjs(report.reportDate).format("DD MMM YYYY")}</td>
                        {FIELDS.map((field) => (
                          <td key={field.key} className="daily-num">
                            <span className="daily-count">{report[field.key] ?? 0}</span>
                          </td>
                        ))}
                        <td className="camp-nowrap">
                          {report.updatedAt ? dayjs(report.updatedAt).format("DD MMM, hh:mm A") : "—"}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={FIELDS.length + 2} className="camp-empty">
                      <div className="camp-empty-inner">
                        <strong>No reports yet</strong>
                        <p>A saved report will show here, including earlier dates.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </form>
    </div>
  );
};

export default SalesDailyReport;
